import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import LoaderBilan from "../LoaderBilan";

const FormHoldingSelector = ({
  formContent,
  schema,
  submitFunction,
  title,
  action,
  notConfirmMessage,
  sideOpen,
  setSideOpen,
  initialValues = {},
  isSyncing = false
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialValues
  });

  useEffect(() => {
    if (!sideOpen) {
      setFormOpen(false);
    }
  }, [sideOpen]);

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  useEffect(() => {
    formContent.forEach((item) => {
      if (item.type !== "select" || !item.autoSelectFirst || item.loading) {
        return;
      }

      const currentValue = watch(item.id);
      const hasCurrentValue = item.values.some(
        (value) => String(value.value) === String(currentValue)
      );

      if (!hasCurrentValue && item.values.length > 0) {
        setValue(item.id, String(item.values[0].value), {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    });
  }, [formContent, setValue, watch]);

  const toggleForm = () => {
    setFormOpen(!formOpen);
    if (!formOpen) {
      setSideOpen(true);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: submitFunction,
    onSuccess: () => {
      queryClient.invalidateQueries();
      if (notConfirmMessage) {
        setFormOpen(false);
      }
    }
  });

  const onSubmit = (data) => {
    setFormOpen(false);
    mutate(data);
  };

  const getDisplayTitle = () => {
    if (typeof title === "string") {
      if (!sideOpen) {
        return title.charAt(0).toUpperCase();
      }
      return title;
    }

    return title;
  };

  if (typeof title !== "string") {
    return (
      <div className="form-container">
        <button className={`toggle-button ${formOpen ? "button-pushed" : ""}`}>
          <LoaderBilan />
        </button>
      </div>
    );
  }

  return (
    <div className="form-container">
      <button
        className={`toggle-button ${formOpen ? "button-pushed" : ""}`}
        onClick={toggleForm}
      >
        {getDisplayTitle()}
      </button>
      <div className={`form-wrapper ${formOpen ? "form-open" : ""}`}>
        <form className="custom-form" onSubmit={handleSubmit(onSubmit)}>
          {formContent.map((item) => (
            <div key={item.id} className="form-group">
              <label htmlFor={item.id}>{t(item.title)}</label>
              {item.type === "select" ? (
                <select
                  id={item.id}
                  {...register(item.id)}
                  disabled={isPending || isSyncing || item.loading}
                  onChange={(e) => {
                    register(item.id).onChange(e);
                    if (item.change) {
                      item.change(e);
                    }
                  }}
                >
                  {item.values.map((value) => (
                    <option key={value.value} value={value.value}>
                      {t(value.label)}
                    </option>
                  ))}
                </select>
              ) : (
                <input id={item.id} type={item.type} {...register(item.id)} />
              )}
              {errors[item.id] && (
                <span className="error">{t(errors[item.id].message)}</span>
              )}
            </div>
          ))}
          <button type="submit" disabled={isPending || isSyncing}>
            {action === "update" ? t("Modifier") : t("Créer")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FormHoldingSelector;
